// Built-in shaders, written in Figma's shader format (a `main.ts` module with setup/render and
// `defineProperties`), so they run unchanged in Figma and any Figma shader runs here.
//
// Every preset shares one uniform block: size = (width, height, time ms, extra), then three
// vec4 parameter slots a, b, c filled from the controls.

import type { ShaderSource } from './types.ts';

interface PresetDef {
  kind: 'fill' | 'effect';
  name: string;
  description: string;
  /** JS expressions (inside render) for the uniform slots. */
  extra?: string;
  a?: string;
  b?: string;
  c?: string;
  /** Reads frame.time (Figma's isAnimated): the canvas plays it. */
  animated?: boolean;
  /** WGSL: helpers plus `fs_main`. */
  wgsl: string;
  properties: Record<string, Record<string, unknown>>;
}

const color = (r: number, g: number, b: number, a = 1) => ({ r, g, b, a });
const slider = (label: string, defaultValue: number, min: number, max: number, step = 1, unit?: string) => ({ type: 'number', label, defaultValue, control: 'slider', min, max, step, ...(unit ? { unit } : {}) });

const PRESETS: Record<string, PresetDef> = {
  'grain-gradient': {
    kind: 'fill',
    name: 'Grain gradient',
    description: 'A two-color linear gradient with film grain',
    a: '...colorParam("from", [0.99, 0.42, 0.24, 1])',
    b: '...colorParam("to", [0.36, 0.25, 0.92, 1])',
    c: 'numberParam("angle", 135), numberParam("grain", 30), 0, 0',
    wgsl: `
fn hash(p: vec2f) -> f32 { return fract(sin(dot(p, vec2f(12.9898, 78.233))) * 43758.5453); }
@fragment fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let ang = radians(u.c.x);
  let dir = vec2f(sin(ang), -cos(ang));
  let aspect = u.size.x / max(u.size.y, 1.0);
  let p = (uv - vec2f(0.5)) * vec2f(aspect, 1.0);
  let extent = abs(dir.x) * aspect + abs(dir.y);
  let t = clamp(dot(p, dir) / max(extent, 0.0001) + 0.5, 0.0, 1.0);
  let base = mix(u.a, u.b, smoothstep(0.0, 1.0, t));
  let g = (hash(floor(uv * u.size.xy)) - 0.5) * (u.c.y / 100.0) * 0.5;
  return vec4f(clamp(base.rgb + vec3f(g), vec3f(0.0), vec3f(1.0)), base.a);
}`,
    properties: {
      from: { type: 'color', label: 'From', defaultValue: color(0.99, 0.42, 0.24) },
      to: { type: 'color', label: 'To', defaultValue: color(0.36, 0.25, 0.92) },
      angle: slider('Angle', 135, 0, 360, 1, '°'),
      grain: slider('Grain', 30, 0, 100, 1, '%'),
    },
  },
  aurora: {
    kind: 'fill',
    name: 'Aurora',
    description: 'Soft interfering color waves, drifting',
    animated: true,
    extra: 'numberParam("scale", 2)',
    a: '...colorParam("color1", [0.05, 0.08, 0.25, 1])',
    b: '...colorParam("color2", [0.1, 0.85, 0.7, 1])',
    c: '...colorParam("color3", [0.75, 0.35, 0.95, 1])',
    wgsl: `
@fragment fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let aspect = u.size.x / max(u.size.y, 1.0);
  let p = vec2f(uv.x * aspect, uv.y) * u.size.w;
  let t = u.size.z * 0.001;
  let k1 = 0.5 + 0.5 * sin(p.x * 3.0 + sin(p.y * 2.0 + t) * 1.5 + t);
  let k2 = 0.5 + 0.5 * sin(p.y * 4.0 + sin(p.x * 3.0 - t * 0.7) * 1.2);
  var col = mix(u.a.rgb, u.b.rgb, k1 * k1);
  col = mix(col, u.c.rgb, k2 * 0.6);
  return vec4f(col, 1.0);
}`,
    properties: {
      color1: { type: 'color', label: 'Base', defaultValue: color(0.05, 0.08, 0.25) },
      color2: { type: 'color', label: 'Wave', defaultValue: color(0.1, 0.85, 0.7) },
      color3: { type: 'color', label: 'Glow', defaultValue: color(0.75, 0.35, 0.95) },
      scale: slider('Scale', 2, 0.5, 8, 0.1),
    },
  },
  pixelate: {
    kind: 'effect',
    name: 'Pixelate',
    description: 'Square pixels of the layer beneath',
    c: 'numberParam("size", 12), 0, 0, 0',
    wgsl: `
@fragment fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let cell = max(u.c.x, 1.0);
  let px = uv * u.size.xy;
  let center = (floor(px / cell) + vec2f(0.5)) * cell;
  return textureSampleLevel(inputTex, samp, clamp(center / u.size.xy, vec2f(0.0), vec2f(1.0)), 0.0);
}`,
    properties: { size: slider('Pixel size', 12, 1, 100, 1, 'px') },
  },
  duotone: {
    kind: 'effect',
    name: 'Duotone',
    description: 'Maps the layer’s brightness to two colors',
    a: '...colorParam("shadow", [0.11, 0.07, 0.36, 1])',
    b: '...colorParam("highlight", [1, 0.62, 0.42, 1])',
    c: 'numberParam("mix", 100), 0, 0, 0',
    wgsl: `
@fragment fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let col = textureSampleLevel(inputTex, samp, uv, 0.0);
  let un = col.rgb / max(col.a, 0.0001);
  let l = dot(un, vec3f(0.2126, 0.7152, 0.0722));
  let duo = mix(u.a.rgb, u.b.rgb, l);
  let outc = mix(un, duo, u.c.x / 100.0);
  return vec4f(outc * col.a, col.a);
}`,
    properties: {
      shadow: { type: 'color', label: 'Shadow', defaultValue: color(0.11, 0.07, 0.36) },
      highlight: { type: 'color', label: 'Highlight', defaultValue: color(1, 0.62, 0.42) },
      mix: slider('Mix', 100, 0, 100, 1, '%'),
    },
  },
  'chromatic-split': {
    kind: 'effect',
    name: 'Chromatic split',
    description: 'Shifts the red and blue channels apart',
    c: 'numberParam("amount", 6), numberParam("angle", 0), 0, 0',
    wgsl: `
@fragment fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let ang = radians(u.c.y);
  let off = vec2f(cos(ang), sin(ang)) * u.c.x / max(u.size.xy, vec2f(1.0));
  let r = textureSampleLevel(inputTex, samp, clamp(uv + off, vec2f(0.0), vec2f(1.0)), 0.0);
  let g = textureSampleLevel(inputTex, samp, uv, 0.0);
  let b = textureSampleLevel(inputTex, samp, clamp(uv - off, vec2f(0.0), vec2f(1.0)), 0.0);
  return vec4f(r.r, g.g, b.b, max(max(r.a, g.a), b.a));
}`,
    properties: { amount: slider('Amount', 6, 0, 50, 1, 'px'), angle: slider('Angle', 0, 0, 360, 1, '°') },
  },
};

/** Names of the built-in shaders. */
export const SHADER_PRESETS = Object.keys(PRESETS);

export function shaderPresetInfo(name: string): { kind: 'fill' | 'effect'; name: string; description: string } | null {
  const p = PRESETS[name];
  return p ? { kind: p.kind, name: p.name, description: p.description } : null;
}

/** A built-in shader as a fill or effect source (null if there's no such preset). */
export function shaderPreset(name: string): (ShaderSource & { kind: 'fill' | 'effect' }) | null {
  const p = PRESETS[name.toLowerCase().replace(/[\s_]+/g, '-')];
  if (!p) return null;
  return { kind: p.kind, name: p.name, source: moduleSource(p) };
}

function moduleSource(p: PresetDef): string {
  const effect = p.kind === 'effect';
  return `// ${p.name}: ${p.description}. Vellum built-in, in Figma's shader format.
import { defineProperties } from "figma:shaders"

export default function ${effect ? 'Effect' : 'Fill'}() {}

export function setup(device, frame) {
  var wgsl = \`
diagnostic(off,derivative_uniformity);
struct Uniforms {
  size: vec4f,
  a: vec4f,
  b: vec4f,
  c: vec4f,
};
@group(0) @binding(0) var<uniform> u: Uniforms;
${effect ? '@group(0) @binding(1) var samp: sampler;\n@group(0) @binding(2) var inputTex: texture_2d<f32>;\n' : ''}
struct VsIn {
  @location(0) pos: vec2f,
  @location(1) uv: vec2f,
};
struct VsOut {
  @builtin(position) position: vec4f,
  @location(0) uv: vec2f,
};
@vertex fn vs_main(v: VsIn) -> VsOut {
  var out: VsOut;
  out.position = vec4f(v.pos, 0.0, 1.0);
  out.uv = v.uv;
  return out;
}
${p.wgsl.trim()}
\`
  frame.state.module = device.createShaderModule({ code: wgsl })
  frame.state.pipeline = null
  frame.state.pipelineFormat = null
  frame.state.quad = device.createBuffer({ size: 6 * 4 * 4, usage: GPUBufferUsage.VERTEX, mappedAtCreation: true })
  new Float32Array(frame.state.quad.getMappedRange()).set([
    -1, -1, 0, 1,   1, -1, 1, 1,   -1, 1, 0, 0,
    -1, 1, 0, 0,    1, -1, 1, 1,    1, 1, 1, 0,
  ])
  frame.state.quad.unmap()
  frame.state.uniformBuf = device.createBuffer({ size: 64, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST })
${
  effect
    ? `  frame.state.sampler = device.createSampler({ magFilter: 'linear', minFilter: 'linear', addressModeU: 'clamp-to-edge', addressModeV: 'clamp-to-edge' })
  frame.state.placeholder = device.createTexture({ size: [1, 1, 1], format: 'rgba8unorm', usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST })
  device.queue.writeTexture({ texture: frame.state.placeholder }, new Uint8Array([0, 0, 0, 0]), { bytesPerRow: 4 }, { width: 1, height: 1, depthOrArrayLayers: 1 })
`
    : ''
}}

export function render(device, frame) {
  var params = frame.params || {}
  function finiteNumber(value, fallback) {
    var num = Number(value)
    return Number.isFinite(num) ? num : fallback
  }
  function numberParam(name, fallback) {
    return finiteNumber(params[name], fallback)
  }
  function colorParam(name, fallback) {
    var value = params[name] || {}
    return [finiteNumber(value.r, fallback[0]), finiteNumber(value.g, fallback[1]), finiteNumber(value.b, fallback[2]), finiteNumber(value.a, fallback[3])]
  }
  var output = frame.output || {}
  var width = Math.max(1, finiteNumber(output.width, 1))
  var height = Math.max(1, finiteNumber(output.height, 1))
  var time = ${p.animated ? 'finiteNumber(frame.time, 0)' : '0'}
  var a = [${p.a ?? '0, 0, 0, 0'}]
  var b = [${p.b ?? '0, 0, 0, 0'}]
  var c = [${p.c ?? '0, 0, 0, 0'}]
  device.queue.writeBuffer(frame.state.uniformBuf, 0, new Float32Array([width, height, time, ${p.extra ?? '0'}].concat(a, b, c)))

  var format = frame.output.format
  if (frame.state.pipeline == null || frame.state.pipelineFormat !== format) {
    frame.state.pipeline = device.createRenderPipeline({
      layout: 'auto',
      vertex: {
        module: frame.state.module,
        entryPoint: 'vs_main',
        buffers: [{ arrayStride: 16, attributes: [{ shaderLocation: 0, format: 'float32x2', offset: 0 }, { shaderLocation: 1, format: 'float32x2', offset: 8 }] }],
      },
      fragment: { module: frame.state.module, entryPoint: 'fs_main', targets: [{ format: format }] },
      primitive: { topology: 'triangle-list' },
    })
    frame.state.pipelineFormat = format
  }
  var entries = [{ binding: 0, resource: { buffer: frame.state.uniformBuf } }]
${effect ? `  entries.push({ binding: 1, resource: frame.state.sampler })
  entries.push({ binding: 2, resource: frame.input != null ? frame.input.createView() : frame.state.placeholder.createView() })
` : ''}  var bindGroup = device.createBindGroup({ layout: frame.state.pipeline.getBindGroupLayout(0), entries: entries })
  var encoder = device.createCommandEncoder()
  var pass = encoder.beginRenderPass({ colorAttachments: [{ view: frame.output.createView(), loadOp: 'clear', clearValue: { r: 0, g: 0, b: 0, a: 0 }, storeOp: 'store' }] })
  pass.setPipeline(frame.state.pipeline)
  pass.setBindGroup(0, bindGroup)
  pass.setVertexBuffer(0, frame.state.quad)
  pass.draw(6)
  pass.end()
  device.queue.submit([encoder.finish()])
}

defineProperties(${effect ? 'Effect' : 'Fill'}, ${JSON.stringify(p.properties, null, 2)})
`;
}
